import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import {
  useLazyGetRolesQuery,
  useAddRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useSetMessagingFromRoleMutation,
  useLazyGetMessagingFromRoleQuery,
} from 'app/services/system.service'
import { FormInput } from 'components/kendo/form/FormComponents'
import { requiredValidator } from 'components/kendo/form/FormValidators'
import CustomModal from 'components/modals/CustomModal'
import CustomButton from 'components/button/CustomButton'
import CustomIcon from 'components/icon/CustomIcon'
import { icons } from 'utils/icons'
import SearchInput from 'components/input/search-input/SearchInput'
import CustomText from 'components/text/CustomText'
import { BUTTONS, PLACEHOLDERS, ROLES } from 'locales/locales'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@progress/kendo-react-inputs'
import { FixMeLater } from 'types'

// Interface definitions
interface Role {
  _id: string
  name: string
}

interface RoleManagementProps {
  selectedRole: Role | null
  onRoleSelect: (role: Role) => void
}

const RoleManagement: React.FC<RoleManagementProps> = ({ selectedRole, onRoleSelect }) => {
  const { t } = useTranslation()
  // State management
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showMessagingModal, setShowMessagingModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [deletingRole, setDeletingRole] = useState<Role | null>(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedRolesForMessaging, setSelectedRolesForMessaging] = useState<string[]>([])
  const [roleMessaging, setRoleMessaging] = useState<
    { _id: string; name: string; selected: boolean }[]
  >([])

  // API hooks
  const [lazyGetRoles] = useLazyGetRolesQuery()
  const [addRole] = useAddRoleMutation()
  const [updateRole] = useUpdateRoleMutation()
  const [deleteRole] = useDeleteRoleMutation()
  const [lazyGetMessagingFromRole] = useLazyGetMessagingFromRoleQuery()
  const [setMessagingFromRole] = useSetMessagingFromRoleMutation()

  // Fetch roles on component mount
  useEffect(() => {
    fetchRoles()
  }, [])

  // Auto-select first role when roles are loaded and no role is selected
  useEffect(() => {
    if (roles.length > 0 && !selectedRole) {
      onRoleSelect(roles[0])
    }
  }, [roles, selectedRole, onRoleSelect])

  useEffect(() => {
    if (selectedRole) {
      getMessagingFromRole(selectedRole)
    }
  }, [selectedRole])

  const getMessagingFromRole = async (selectedRole: Role) => {
    const response = await lazyGetMessagingFromRole(selectedRole._id)
    if (response.data?.data) {
      setRoleMessaging(response.data?.data.messaging || [])
    }
  }

  const fetchRoles = async () => {
    setLoading(true)
    try {
      const response = await lazyGetRoles({
        skip: 0,
        take: 100,
        sort: -1,
        count: true,
      })
      setRoles(response.data?.data || [])
    } catch (error) {
      console.error('Error fetching roles:', error)
    }
    setLoading(false)
  }

  const handleRoleSelect = (role: Role) => {
    onRoleSelect(role)
  }

  const handleAddRole = () => {
    setShowAddModal(true)
  }

  const handleEditRole = (role: Role, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingRole(role)
    setShowEditModal(true)
  }

  const handleCloseModals = () => {
    setShowAddModal(false)
    setShowEditModal(false)
    setShowMessagingModal(false)
    setShowDeleteModal(false)
    setEditingRole(null)
    setDeletingRole(null)
    setModalLoading(false)
    setSelectedRolesForMessaging([])
  }

  const handleOpenMessagingModal = () => {
    setShowMessagingModal(true)
  }

  const handleMessagingRoleToggle = (roleId: string) => {
    setSelectedRolesForMessaging((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId],
    )

    setRoleMessaging((prev) =>
      prev.map((role) => (role._id === roleId ? { ...role, selected: !role.selected } : role)),
    )
  }

  const handleSubmitMessaging = async () => {
    const selectedRoleIds = roleMessaging.filter((role) => role.selected).map((role) => role._id)

    await setMessagingFromRole({
      _id: selectedRole?._id,
      messagings: selectedRoleIds,
    })
    await fetchRoles()
    handleCloseModals()
  }

  const handleSubmitAdd = async (dataItem: FixMeLater) => {
    setModalLoading(true)
    try {
      const roleData = {
        name: dataItem.name.trim(),
      }

      await addRole(roleData).unwrap()
      await fetchRoles() // Refresh roles list
      handleCloseModals()
    } catch (error) {
      console.error('Error adding role:', error)
    }
    setModalLoading(false)
  }

  const handleDeleteRoleClick = (role: Role, e: React.MouseEvent) => {
    e.stopPropagation()
    setDeletingRole(role)
    setShowDeleteModal(true)
  }

  const handleConfirmDeleteRole = async () => {
    if (!deletingRole) return

    setModalLoading(true)
    try {
      await deleteRole({ _id: deletingRole._id }).unwrap()
      await fetchRoles()
      const index = roles.findIndex((r) => r._id === deletingRole._id)
      onRoleSelect(roles[index - 1] || roles[0])
      handleCloseModals()
    } catch (error) {
      console.error('Error deleting role:', error)
    }
    setModalLoading(false)
  }

  const handleSubmitEdit = async (dataItem: FixMeLater) => {
    if (!editingRole) return

    setModalLoading(true)
    try {
      const roleData = {
        _id: editingRole._id,
        name: dataItem.name.trim(),
      }

      await updateRole(roleData).unwrap()
      await fetchRoles() // Refresh roles list

      // Update selected role if it was the one being edited
      if (selectedRole?._id === editingRole._id) {
        onRoleSelect({ ...editingRole, name: dataItem.name.trim() })
      }

      handleCloseModals()
    } catch (error) {
      console.error('Error updating role:', error)
    }
    setModalLoading(false)
  }

  const filteredRoles = roles.filter((role) =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
    <>
      <LeftPanel>
        <TitleContainer>
          <StyledCustomText typography="header1">{t(ROLES.MAIN_ROLES)}</StyledCustomText>
        </TitleContainer>

        <FilterContainer>
          <FilterLeft>
            <AddButton text={t(BUTTONS.ADD)} onClick={handleAddRole} />

            <MessagingButton
              text={t(BUTTONS.MESSAGING)}
              onClick={handleOpenMessagingModal}
              disabled={!selectedRole}
            />
          </FilterLeft>
          <FilterRight>
            <SearchInput
              placeHolder={t(PLACEHOLDERS.SEARCH_PLACEHOLDER)}
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
            />
          </FilterRight>
        </FilterContainer>

        <PanelContent>
          <RolesList>
            {filteredRoles.map((role) => (
              <RoleItem
                key={role._id}
                selected={selectedRole?._id === role._id}
                onClick={() => handleRoleSelect(role)}
              >
                <RoleDetails>
                  <RoleName>{role.name}</RoleName>
                </RoleDetails>
                <RoleActions>
                  <StyledCustomIcon
                    source={icons.editIcon}
                    onClick={(e) => handleEditRole(role, e)}
                    title={t(BUTTONS.EDIT)}
                  />
                  <StyledCustomIcon
                    source={icons.deleteIcon}
                    onClick={(e) => handleDeleteRoleClick(role, e)}
                    title={t(BUTTONS.DELETE)}
                  />
                </RoleActions>
              </RoleItem>
            ))}
          </RolesList>
        </PanelContent>
      </LeftPanel>

      {/* Add Role Modal */}
      {showAddModal && (
        <CustomModal
          title={t(ROLES.ADD_ROLE_TITLE)}
          closeModal={handleCloseModals}
          loading={modalLoading}
          showButtons={false}
        >
          <Form
            onSubmit={handleSubmitAdd}
            render={(formRenderProps) => (
              <FormElement>
                <Field
                  name="name"
                  component={FormInput}
                  label={t(ROLES.ROLE_NAME_TITLE)}
                  validator={requiredValidator}
                />
                <ModalButtons>
                  <CustomButton
                    text={t(BUTTONS.CANCEL)}
                    type="secondary"
                    onClick={handleCloseModals}
                    disabled={modalLoading}
                  />
                  <CustomButton
                    text={t(BUTTONS.ADD)}
                    type="primary"
                    onClick={() => formRenderProps.onSubmit({} as any)}
                    disabled={!formRenderProps.valid || modalLoading}
                  />
                </ModalButtons>
              </FormElement>
            )}
          />
        </CustomModal>
      )}

      {/* Edit Role Modal */}
      {showEditModal && editingRole && (
        <CustomModal
          title={t(ROLES.EDIT_ROLE_TITLE)}
          closeModal={handleCloseModals}
          loading={modalLoading}
          showButtons={false}
        >
          <Form
            initialValues={{ name: editingRole.name }}
            onSubmit={handleSubmitEdit}
            render={(formRenderProps) => (
              <FormElement>
                <Field
                  name="name"
                  component={FormInput}
                  label={t(ROLES.ROLE_NAME_TITLE)}
                  validator={requiredValidator}
                />
                <ModalButtons>
                  <CustomButton
                    text={t(BUTTONS.CANCEL)}
                    type="secondary"
                    onClick={handleCloseModals}
                    disabled={modalLoading}
                  />
                  <CustomButton
                    text={t(BUTTONS.SAVE)}
                    type="primary"
                    onClick={() => formRenderProps.onSubmit({} as any)}
                    disabled={!formRenderProps.valid || modalLoading}
                  />
                </ModalButtons>
              </FormElement>
            )}
          />
        </CustomModal>
      )}

      {/* Messaging Modal */}
      {showMessagingModal && (
        <CustomModal
          title={t(ROLES.MESSAGING_ROLE_SELECTION)}
          closeModal={handleCloseModals}
          loading={modalLoading}
          showButtons={false}
        >
          <MessagingForm>
            <MessagingDescription>
              {t(ROLES.MESSAGING_ROLE_SELECTION_DESCRIPTION, { roleName: selectedRole?.name })}
            </MessagingDescription>
            <RolesCheckboxList>
              {roleMessaging.map((role) => (
                <RoleCheckboxItem key={role._id}>
                  <CheckboxWithLabel
                    onClick={(e) => {
                      e.stopPropagation()
                      handleMessagingRoleToggle(role._id)
                    }}
                  >
                    <Checkbox
                      checked={role.selected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleMessagingRoleToggle(role._id)}
                    />
                    <CheckboxLabel>{role.name}</CheckboxLabel>
                  </CheckboxWithLabel>
                </RoleCheckboxItem>
              ))}
            </RolesCheckboxList>
            <ModalButtons>
              <CustomButton
                text={t(BUTTONS.CANCEL)}
                type="secondary"
                onClick={handleCloseModals}
                disabled={modalLoading}
              />
              <CustomButton
                text={t(BUTTONS.SAVE)}
                type="primary"
                onClick={handleSubmitMessaging}
                disabled={selectedRolesForMessaging.length === 0 || modalLoading}
              />
            </ModalButtons>
          </MessagingForm>
        </CustomModal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingRole && (
        <CustomModal
          title={t(ROLES.DELETE_ROLE_CONFIRMATION_TITLE)}
          closeModal={handleCloseModals}
          loading={modalLoading}
          showButtons={false}
        >
          <DeleteConfirmationForm>
            <DeleteConfirmationText>
              {t(ROLES.DELETE_ROLE_CONFIRMATION, { roleName: deletingRole.name })}
            </DeleteConfirmationText>
            <DeleteWarningText>{t(ROLES.DELETE_ROLE_WARNING)}</DeleteWarningText>
            <ModalButtons>
              <CustomButton
                text={t(BUTTONS.CANCEL)}
                type="secondary"
                onClick={handleCloseModals}
                disabled={modalLoading}
              />
              <DeleteButton
                text={t(BUTTONS.DELETE)}
                type="primary"
                onClick={handleConfirmDeleteRole}
                disabled={modalLoading}
              />
            </ModalButtons>
          </DeleteConfirmationForm>
        </CustomModal>
      )}
    </>
  )
}

export default RoleManagement

// Styled Components
const StyledCustomText = styled(CustomText)`
  word-wrap: normal;
  white-space: nowrap;
  @media (max-width: 1024px) {
    font-size: 12px;
  }
`

const LeftPanel = styled.div`
  flex: 0 0 25%;
  border: 1px solid #e3ebf6;
  border-radius: 4px;
  overflow: hidden;

  @media (max-width: 1024px) {
    flex: none;
    min-height: 300px;
  }
`

const TitleContainer = styled.div`
  padding: 10px 14px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e3ebf6;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;

  @media (max-width: 1024px) {
    justify-content: center;
    padding: 16px;
  }
`

const FilterContainer = styled.div`
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e3ebf6;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 16px;
  }
`

const FilterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;

  @media (max-width: 1024px) {
    justify-content: space-between;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }
`

const FilterRight = styled.div`
  flex: 0 0 150px;

  @media (max-width: 1024px) {
    flex: none;
    width: 100%;
  }
`

const AddButton = styled(CustomButton)`
  height: 32px;
`

const MessagingButton = styled(CustomButton)`
  background-color: #28a745;
  height: 32px;
  &:hover {
    background-color: #0f4e1d;
  }
`

const PanelContent = styled.div`
  padding: 16px;
  height: calc(100% - 96px);
  overflow-y: auto;
`

const RolesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledCustomIcon = styled(CustomIcon)`
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    border-radius: 28px;
    background-color: #e0e0e0;
  }
  cursor: pointer;
`

const RoleItem = styled.div<{ selected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 1px solid ${(props) => (props.selected ? '#007acc' : '#e3ebf6')};
  border-radius: 4px;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? '#f0f8ff' : 'white')};
  transition: all 0.2s ease;

  &:hover {
    border-color: #007acc;
    background-color: #f0f8ff;
  }
`

const RoleDetails = styled.div`
  flex: 1;
`

const RoleName = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
`

const RoleActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 8px;
`

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e3ebf6;
`

const MessagingForm = styled.div`
  padding: 16px 0;
`

const MessagingDescription = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
`

const RolesCheckboxList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 24px;
`

const RoleCheckboxItem = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`

const CheckboxWithLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: #333;
  user-select: none;
  cursor: pointer;
`

const DeleteConfirmationForm = styled.div`
  padding: 16px 0;
`

const DeleteConfirmationText = styled.div`
  font-size: 16px;
  color: #333;
  margin-bottom: 12px;
  text-align: center;
`

const DeleteWarningText = styled.div`
  font-size: 14px;
  color: #dc3545;
  margin-bottom: 24px;
  text-align: center;
  font-style: italic;
`

const DeleteButton = styled(CustomButton)`
  background-color: #dc3545 !important;
  border-color: #dc3545 !important;
  
  &:hover {
    background-color: #c82333 !important;
    border-color: #bd2130 !important;
  }
`
